import Const from 'Utils/Consts'
import QueryStringBuilder from 'Utils/QueryStringBuilder.Utilts'
import userManager from 'Oidc/userManager'
import { type HttpMethod, Method } from './httpMethod'
import { SigninResponse, User } from 'oidc-client-ts'
import { baseUrl, identityInstance, instance } from './instance'
import type { BaseQueryFn } from '@reduxjs/toolkit/dist/query'
import type { AxiosRequestConfig, AxiosError } from 'axios'

// ------------------------------
//  RTK Query Base
// ------------------------------

const axiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string } = { baseUrl: '' },
  ): BaseQueryFn<
    {
      url: string
      method?: HttpMethod
      data?: AxiosRequestConfig['data']
      params?: AxiosRequestConfig['params']
      headers?: AxiosRequestConfig['headers']
    } & AxiosRequestConfig,
    unknown,
    unknown
  > =>
  async ({ url, method, data, params, headers, ...others }, api) => {
    try {
      const result = await instance({
        url: baseUrl + url,
        method,
        data,
        params,
        headers,
        ...others,
      })

      return { data: result.data }
    } catch (axiosError) {
      const err = axiosError as AxiosError
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      }
    }
  }

// ------------------------------
//  Refresh Token Logic
// ------------------------------

async function refreshToken(state: User): Promise<User> {
  const result = await identityInstance.request({
    url: 'oauth2/token',
    method: Method.POST,
    data: QueryStringBuilder({
      grant_type: 'refresh_token',
      refresh_token: state.refresh_token,
      scope: state.scope,
      client_id: Const.env.REACT_APP_CLIENT_ID,
      code_verifier: String(localStorage.getItem('code_verifier')),
    }),
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  })

  if (result.status === 400) throw new Error('Error_On_Refresh_Token')

  const response = new SigninResponse(new URLSearchParams())
  Object.assign(response, result.data)

  const updatedUser = new User({
    ...state,
    ...response,
    profile: state.profile,
  })

  // store updated user
  const userStoreKey = `user:${userManager.settings.authority}:${userManager.settings.client_id}`

  if (updatedUser) {
    const storageString = updatedUser.toStorageString()
    await userManager.settings.userStore.set(userStoreKey, storageString)
  } else {
    await userManager.settings.userStore.remove(userStoreKey)
  }

  resetConfigs()

  // update axios header globally
  instance.interceptors.request.use((config: any) => {
    config.headers.Authorization = `${Const.env.REACT_APP_REACT_FAKE_USER == false ? 'Bearer' : 'Fake'} ${updatedUser.access_token}`
    // config.headers.Authorization = `Bearer ${updatedUser.access_token}`
    return config
  })

  return updatedUser
}

// ------------------------------
//  Helpers
// ------------------------------

const resetConfigs = () => {
  instance.defaults.baseURL = baseUrl
  instance.defaults.timeout = 20000
  // instance.defaults.headers["Content-Type"] = "application/json";
}

export { axiosBaseQuery, refreshToken, resetConfigs }
